import React from "react";
import { Link } from "react-router-dom";
import { IMAGES, QUOTES, SOLUTIONS, MILESTONES } from "@/mock";
import LogCravingDialog, { intensityColor } from "@/components/LogCravingDialog";
import DynIcon from "@/components/DynIcon";
import { LifeBuoy, Flame, PiggyBank, ShieldCheck, Plus, ArrowRight, Quote, CalendarCheck, Sparkles, Gift, MessageCircleHeart, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { imgSet } from "@/lib/img";

const DAY = 86400000;

export const greetingFor = (hour) => {
  if (hour < 12) return "Bonjour";
  if (hour < 18) return "Bon après-midi";
  return "Bonsoir";
};

export const trialDaysLeft = (user) => {
  if (user?.plan_interval !== "trial" || !user.plan_expires_at) return null;
  return Math.max(0, Math.ceil((new Date(user.plan_expires_at) - Date.now()) / DAY));
};

function milestoneProgress(streakDays) {
  const next = MILESTONES.find((m) => m.days > streakDays) || MILESTONES[MILESTONES.length - 1];
  const prevDays = [...MILESTONES].reverse().find((m) => m.days <= streakDays)?.days || 0;
  const pct = Math.min(100, Math.round(((streakDays - prevDays) / (next.days - prevDays)) * 100));
  return { next, pct };
}

const Stat = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <div className="lift rounded-3xl border border-[#E9E1D6] bg-white p-5" data-testid={testid}>
    <div className="mb-4 grid h-10 w-10 place-items-center rounded-2xl" style={{ background: tone + "22", color: tone }}>
      <Icon size={20} />
    </div>
    <div className="font-display text-3xl font-semibold text-[#1F2A24]">{value}</div>
    <div className="mt-1 text-sm font-medium text-[#4A5A50]">{label}</div>
    {sub && <div className="mt-0.5 text-xs text-[#8A958E]">{sub}</div>}
  </div>
);

export function HeroSection({ greeting, name, stats }) {
  const { next, pct } = milestoneProgress(stats.streakDays);
  return (
  <section className="grid gap-6 lg:grid-cols-[1.35fr_1fr]">
    <div className="fade-up relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#EAF0E6] to-[#F6EDE2] p-7 md:p-10">
      <p className="text-sm font-medium uppercase tracking-[0.18em] text-[#5F7F6A]">{format(new Date(), "EEEE d MMMM", { locale: fr })}</p>
      <h1 className="font-display mt-3 text-4xl font-semibold leading-[1.05] text-[#1F2A24] md:text-6xl" data-testid="home-greeting">
        {greeting}{name ? `, ${name}` : ""}.<br />
        <span className="italic text-[#2F4A3A]">{stats.total ? "Tu tiens bon." : "Bienvenue."}</span>
      </h1>
      <p className="mt-5 max-w-md text-base leading-relaxed text-[#4A5A50]">
        Une envie de sucre arrive ? Elle dure en moyenne 10 à 15 minutes. Laisse-nous t'accompagner jusqu'à ce qu'elle passe.
      </p>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Link to="/sos" data-testid="hero-sos-btn" className="group relative inline-flex items-center gap-3 rounded-full bg-[#E8735A] px-7 py-4 text-base font-semibold text-white shadow-[0_14px_34px_-12px_rgba(232,115,90,.8)] transition-colors hover:bg-[#D9634A]">
          <span className="absolute inset-0 rounded-full bg-[#E8735A] pulse-ring" />
          <LifeBuoy size={20} className="relative" />
          <span className="relative">J'ai une envie maintenant</span>
        </Link>
        <LogCravingDialog>
          <button className="inline-flex items-center gap-2 rounded-full border border-[#2F4A3A]/20 bg-white/70 px-6 py-4 text-sm font-semibold text-[#2F4A3A] transition-colors hover:bg-white" data-testid="hero-log-btn">
            <Plus size={18} /> Noter une envie
          </button>
        </LogCravingDialog>
      </div>
    </div>

    <div className="fade-up d1 relative overflow-hidden rounded-[2rem] bg-[#2F4A3A] p-7 text-[#FBF7F2] md:p-8" data-testid="streak-card">
      <img {...imgSet(IMAGES.calm)} loading="lazy" decoding="async" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="relative">
        <div className="flex items-center gap-2 text-sm text-[#F2C46D]"><Flame size={16} /> Série en cours</div>
        <div className="mt-4 flex items-end gap-3">
          <span className="font-display text-8xl font-semibold leading-none" data-testid="streak-days">{stats.streakDays}</span>
          <span className="pb-3 text-lg">jour{stats.streakDays > 1 ? "s" : ""}<br /><span className="text-sm opacity-70">+ {stats.streakHours} h</span></span>
        </div>
        <p className="mt-4 text-sm opacity-80">sans craquer</p>
        <div className="mt-8">
          <div className="mb-2 flex justify-between text-xs opacity-80"><span>Prochain palier : {next.title}</span><span>{pct}%</span></div>
          <div className="h-2 overflow-hidden rounded-full bg-white/15">
            <div className="h-full rounded-full bg-[#F2C46D] transition-[width] duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-3 text-xs leading-relaxed opacity-75">{next.benefit}</p>
        </div>
      </div>
    </div>
  </section>
  );
}

export function StatsSection({ stats }) {
  return (
  <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
    <div className="fade-up d1"><Stat testid="stat-resisted" icon={ShieldCheck} label="Envies résistées" value={stats.resisted} sub={`sur ${stats.total} notées`} tone="#2F4A3A" /></div>
    <div className="fade-up d2"><Stat testid="stat-rate" icon={Flame} label="Taux de réussite" value={`${stats.successRate}%`} sub="depuis le début" tone="#E8735A" /></div>
    <div className="fade-up d3"><Stat testid="stat-money" icon={PiggyBank} label="Économisés" value={`${stats.moneySaved.toFixed(0)} €`} sub={`${stats.sugarFreeDays} jours sans écart`} tone="#C9922E" /></div>
    <div className="fade-up d4"><Stat testid="stat-today" icon={CalendarCheck} label="Envies aujourd'hui" value={stats.today.length} sub={stats.today.length ? "tu les gères" : "journée calme"} tone="#5F7F6A" /></div>
  </section>
  );
}

export function TipQuoteSection() {
  const dayIdx = Math.floor(Date.now() / DAY);
  const quote = QUOTES[dayIdx % QUOTES.length];
  const tip = SOLUTIONS[dayIdx % SOLUTIONS.length];
  return (
  <section className="grid gap-6 lg:grid-cols-3">
    <Link to="/solutions" className="lift group overflow-hidden rounded-3xl border border-[#E9E1D6] bg-white lg:col-span-2" data-testid="tip-of-day">
      <div className="grid h-full md:grid-cols-2">
        <img {...imgSet(IMAGES.berries)} loading="lazy" decoding="async" alt="Fruits rouges" className="h-48 w-full object-cover md:h-full" />
        <div className="flex flex-col p-6">
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#E8735A]">Solution du jour · {tip.category}</span>
          <h3 className="font-display mt-3 flex items-center gap-2 text-2xl font-semibold"><DynIcon name={tip.icon} size={22} className="text-[#2F4A3A]" /> {tip.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#4A5A50]">{tip.summary}</p>
          <span className="mt-auto inline-flex items-center gap-1 pt-6 text-sm font-semibold text-[#2F4A3A]">Découvrir toutes les solutions <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></span>
        </div>
      </div>
    </Link>

    <div className="flex flex-col justify-between rounded-3xl bg-[#F6E3DA] p-6">
      <Quote className="text-[#E8735A]" size={28} />
      <p className="font-display mt-4 text-xl italic leading-snug text-[#1F2A24]" data-testid="quote-of-day">{quote}</p>
      <span className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-[#8A6A5E]">Pensée du jour</span>
    </div>
  </section>
  );
}

export function TrialBanner({ daysLeft: trialDaysLeft }) {
  return (
  <Link to="/tarifs" className="lift flex flex-wrap items-center gap-3 rounded-3xl border border-[#EAD9B8] bg-[#FFF8EA] px-6 py-4" data-testid="home-trial-banner">
    <Gift size={20} className="text-[#C9922E]" />
    <span className="flex-1 text-sm"><b>Essai Premium</b> : encore {trialDaysLeft} jour{trialDaysLeft > 1 ? "s" : ""} pour profiter du coach IA et du programme 30 jours.</span>
    <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#2F4A3A]">Garder Premium <ArrowRight size={15} /></span>
  </Link>
  );
}

export function UpsellSection() {
  return (
  <section className="relative overflow-hidden rounded-[2rem] bg-[#2F4A3A] p-7 text-white md:p-10" data-testid="home-upsell">
    <div className="grid items-center gap-6 md:grid-cols-[1.3fr_1fr]">
      <div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F2C46D] px-3 py-1 text-xs font-bold text-[#1F2A24]"><Sparkles size={12} /> Aller plus loin</span>
        <h2 className="font-display mt-4 text-3xl font-semibold md:text-4xl">Des résultats qui durent</h2>
        <p className="mt-3 max-w-md text-white/80">Programme guidé de 30 jours, recettes sans sucre et coach IA 24h/24. À partir de 4,99 € par mois. 7 jours de Premium offerts à l'inscription.</p>
        <Link to="/tarifs" data-testid="home-upsell-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F2C46D] px-6 py-3 font-semibold text-[#1F2A24] transition-colors hover:bg-[#EAB650]">Voir les formules <ArrowRight size={16} /></Link>
      </div>
      <div className="grid gap-3">
        {[[CalendarDays, "Programme 30 jours", "Standard"], [MessageCircleHeart, "Coach IA 24h/24", "Premium"], [Sparkles, "Statistiques avancées", "Basic"]].map(([I, t, p]) => (
          <div key={t} className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 backdrop-blur"><I size={18} className="text-[#F2C46D]" /><span className="flex-1 text-sm font-medium">{t}</span><span className="text-xs text-white/60">{p}</span></div>
        ))}
      </div>
    </div>
  </section>
  );
}

export function RecentCravings({ items }) {
  return (
  <section>
    <div className="mb-4 flex items-end justify-between">
      <h2 className="font-display text-2xl font-semibold">Dernières envies</h2>
      <Link to="/journal" className="-my-2 py-3 text-sm font-semibold text-[#2F4A3A] hover:underline" data-testid="see-journal-link">Voir le journal</Link>
    </div>
    <div className="divide-y divide-[#EFE7DC] overflow-hidden rounded-3xl border border-[#E9E1D6] bg-white">
      {items.slice(0, 4).map((c) => (
        <div key={c.id} className="flex items-center gap-4 px-5 py-4">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full font-display text-sm font-semibold text-white" style={{ background: intensityColor(c.intensity) }}>{c.intensity}</span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{c.trigger} <span className="font-normal text-[#8A958E]">· {c.emotion}</span></div>
            <div className="truncate text-xs text-[#8A958E]">{format(new Date(c.date), "d MMM, HH:mm", { locale: fr })}{c.strategy ? ` · ${c.strategy}` : ""}</div>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${c.outcome === "resisted" ? "bg-[#EAF0E6] text-[#2F4A3A]" : "bg-[#FBE4DD] text-[#B84E37]"}`}>
            {c.outcome === "resisted" ? "Résisté" : "Craqué"}
          </span>
        </div>
      ))}
      {!items.length && <div className="px-5 py-10 text-center text-sm text-[#8A958E]">Aucune envie notée pour l'instant.</div>}
    </div>
  </section>
  );
}
